import React from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { PdpSummaryHero } from 'features/app-takeover/components/pdp-summary-hero';
import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { StyleSheet } from 'react-native';

import { NutritionInformation } from 'components/product-detail-v2/nutrition-information';
import { OrderSummary } from 'components/product-detail-v2/order-summary';
import { ProductCta } from 'components/product-detail-v2/product-cta';
import { ProductUpsell } from 'components/product-detail-v2/product-upsell';
import { transformProductWizardStepToEventAttr } from 'components/product-detail-v2/utils';
import { Box } from 'components/ui';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';
import { primitive } from 'styles/constants';
import { isCombo } from 'utils/menu';

import { ProductDetailHeader } from '../product-detail-header';

import { OrderSummaryHeader, OrderSummaryPanel } from './order-summary-panel';
import {
  ProductOrderReviewContainer,
  ProductOrderReviewInnerLayout,
  ProductOrderReviewWrapper,
  ProductReviewLayoutContainer,
} from './styled';
import { IProductOrderReview } from './types';

export const ProductOrderReview: React.FC<IProductOrderReview> = ({ modifierSelections }) => {
  const { back } = useRouter();
  const {
    currentStep,
    goToHome,
    goToCustomizeProduct,
    isProductReviewFromCart,
    id,
  } = useProductWizardFlow();

  const {
    backgroundColor: takeoverBg,
    summaryPageBackgroundImage,
    summaryPageBackgroundImageStyle,
  } = useAppTakeoverPdp(id);
  const {
    menuObject,
    productWizardFlowSteps,
    resetProductWizard,
    setIsBackNavigation,
    selectedProduct,
    userSelections,
  } = useProductWizardContext();

  const isMobile = useIsMobileBreakpoint();

  const foundFirstStep = productWizardFlowSteps.find(step => !!step.isFirstStep);
  const shouldRedirectToHome = !foundFirstStep && !isProductReviewFromCart;

  React.useEffect(() => {
    if (shouldRedirectToHome) {
      resetProductWizard();
      goToHome();
    }
  }, [goToHome, resetProductWizard, shouldRedirectToHome]);

  const lastScreen = productWizardFlowSteps.at(-1);

  const handleBackNavigation = React.useCallback(() => {
    setIsBackNavigation(true);

    if (isProductReviewFromCart) {
      return back();
    }

    return goToCustomizeProduct({
      slotKey: lastScreen?.id ?? '',
      step: ProductWizardStep.ProductCustomization,
    });
  }, [back, goToCustomizeProduct, isProductReviewFromCart, lastScreen?.id, setIsBackNavigation]);

  const productDetailHeaderTitle = isCombo(selectedProduct)
    ? t('reviewYourMeal')
    : t('reviewYourSelection');

  const backNavigationLabel =
    (lastScreen && userSelections.comboSlot?.get(lastScreen.id)?.selectedItem?.name.locale) ?? '';

  if (shouldRedirectToHome) {
    // redirecting don't display janky UI
    return null;
  }

  return (
    <ProductOrderReviewWrapper>
      <ProductDetailHeader
        title={productDetailHeaderTitle}
        currentStep={currentStep}
        onPress={handleBackNavigation}
        backgroundColor={primitive.$white}
        backNavigationLabel={t('productDetailPickerCustomizationGoBack', {
          screen: backNavigationLabel,
        })}
        eventAttributes={{ name: transformProductWizardStepToEventAttr(currentStep) }}
      />

      <ScrollView showsVerticalScrollIndicator backgroundColor={takeoverBg || primitive.$white}>
        {summaryPageBackgroundImage ? (
          <Box style={styles.fullWidth} testID="order-review">
            <PdpSummaryHero
              backgroundImage={summaryPageBackgroundImage}
              backgroundImageStyle={summaryPageBackgroundImageStyle}
            >
              <ProductReviewLayoutContainer isFullContainer={isMobile}>
                <ProductOrderReviewInnerLayout>
                  <OrderSummaryHeader />
                </ProductOrderReviewInnerLayout>
              </ProductReviewLayoutContainer>
            </PdpSummaryHero>
            <Box style={styles.fullWidth}>
              <ProductReviewLayoutContainer isFullContainer={isMobile}>
                <ProductOrderReviewInnerLayout>
                  <OrderSummary />
                </ProductOrderReviewInnerLayout>
              </ProductReviewLayoutContainer>
            </Box>
          </Box>
        ) : (
          <Box style={styles.fullWidth}>
            <ProductReviewLayoutContainer isFullContainer={isMobile}>
              <ProductOrderReviewInnerLayout>
                <OrderSummaryPanel />
              </ProductOrderReviewInnerLayout>
            </ProductReviewLayoutContainer>
          </Box>
        )}

        <ProductReviewLayoutContainer
          isFullContainer={isMobile}
          backgroundColor={takeoverBg || primitive.$mayoEggWhite}
        >
          <ProductOrderReviewInnerLayout>
            <ProductOrderReviewContainer>
              <ProductUpsell />
              <NutritionInformation />
            </ProductOrderReviewContainer>
          </ProductOrderReviewInnerLayout>
        </ProductReviewLayoutContainer>
      </ScrollView>

      <ProductCta
        currentStep={currentStep}
        menuObject={menuObject}
        modifierSelections={modifierSelections}
      />
    </ProductOrderReviewWrapper>
  );
};

const styles = StyleSheet.create({
  fullWidth: {
    width: '100%',
  },
});
